import { useToast } from '@chakra-ui/react';
import {
  type ChangeEvent,
  type FormEventHandler,
  useMemo,
  useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';

import { LIST_QUERY_KEY } from '@/pages/camel-kiosk/accounts/index/useCamelKioskAccountsPage';
import { routes } from '@/router';
import {
  getAccount,
  updateStore,
  uploadStoreMainImage,
} from '@/services/Kiosk';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
export const IMAGE_TYPES = [
  'image/jpg',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

const storeMainImageSchema = z
  .instanceof(File, { message: '画像を選択してください' })
  .refine((file) => file.size <= MAX_IMAGE_SIZE, {
    message: '画像のサイズは最大5MBです',
  })
  .refine((file) => IMAGE_TYPES.includes(file.type), {
    message: '画像の形式はjpg, png, jpeg, gif, webpのみです',
  });
const primaryColorSchema = z
  .string()
  .min(2, { message: 'メインカラーを設定してください' })
  .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, {
    message: 'カラーコードの形式が正しくありません',
  });
const privacyPolicyUrlSchema = z
  .string()
  .url({ message: 'URLの形式が正しくありません' });

type Props = {
  accountId: string;
};

export const useStoreSettingForm = (props: Props) => {
  const toast = useToast();
  const { mutateAsync: update, isLoading: isUpdating } =
    useMutation(updateStore);
  const { data: account } = useQuery({
    queryKey: `kiosk-account-${props.accountId}`,
    queryFn: () => getAccount(props.accountId),
  });
  const [handleColor, setHandleColor] = useState<string | null>(null);
  const [storeMainImage, setStoreMainImage] = useState<null | File>(null);

  const [handlePrivacyPolicyUrl, setPrivacyPolicy] = useState<string | null>(
    null
  );
  const [isSubmitFailed, setIsSubmitFailed] = useState(false);
  const queryClient = useQueryClient();
  const history = useHistory();

  const imageSrc = useMemo(() => {
    if (storeMainImage) {
      return URL.createObjectURL(storeMainImage);
    }
    return account?.store_main_image_url || '';
  }, [storeMainImage, account?.store_main_image_url]);
  const primaryColor = useMemo(() => {
    if (handleColor) {
      return handleColor;
    }
    return account?.primary_color_code || '#000000';
  }, [handleColor, account?.primary_color_code]);

  const privacyPolicyUrl = useMemo(() => {
    if (handlePrivacyPolicyUrl) {
      return handlePrivacyPolicyUrl;
    }
    return account?.privacy_policy_url || '';
  }, [handlePrivacyPolicyUrl, account?.privacy_policy_url]);

  const hasStoreMainImageUrl = (account?.store_main_image_url || '').length > 0;
  const hasNewStoreImage = Boolean(storeMainImage);

  const schema = z.object({
    storeMainImage:
      hasNewStoreImage || !hasStoreMainImageUrl
        ? storeMainImageSchema
        : z.null(),
    primaryColor: primaryColorSchema,
    privacyPolicyUrl: privacyPolicyUrlSchema,
  });

  const handleChangeImage = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setStoreMainImage(files[0]);
      setIsSubmitFailed(false);
    }
  };

  const handleChangeColor = (value: string) => {
    const cleanValue = value.replace(/#/g, '').slice(0, 6);
    setHandleColor(`#${cleanValue}`);
    setIsSubmitFailed(false);
  };

  const handleChangePrivacyPolicyUrl = (e: ChangeEvent<HTMLInputElement>) => {
    setPrivacyPolicy(e.target.value);
    setIsSubmitFailed(false);
  };

  const handleUpdate = async (form: {
    storeMainImage: null | File;
    primaryColor: string;
    privacyPolicyUrl: string;
  }) => {
    if (account == null) return;

    let storeMainImageUrl = account.store_main_image_url || '';
    if (form.storeMainImage) {
      storeMainImageUrl = await uploadStoreMainImage(form.storeMainImage);
    }

    await update({
      deliveryStoreId: account.delivery_store_id,
      storeMainImageUrl,
      privacyPolicyUrl: form.privacyPolicyUrl,
      primaryColorCode: form.primaryColor,
    });
    queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    history.push(routes.CamelKioskAccounts.path);
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const formState = {
      storeMainImage,
      primaryColor,
      privacyPolicyUrl,
    };
    const result = schema.safeParse(formState);

    if (result.success) {
      try {
        await handleUpdate(result.data);
        setIsSubmitFailed(false);
        toast({
          title: '保存しました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        setIsSubmitFailed(true);
        toast({
          title: '保存に失敗しました',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      const errMessages = Array.from(
        new Set(result.error.errors.map((e) => e.message))
      );
      setIsSubmitFailed(true);
      toast({
        title: errMessages.join('、'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return {
    isLoading: isUpdating,
    isSubmitFailed,
    handleChangeColor,
    handleChangeImage,
    imageSrc,
    primaryColor,
    handleChangePrivacyPolicyUrl,
    privacyPolicyUrl,
    handleSubmit,
  };
};
